import { computed, effect } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import type { ModeId } from '@vm/shared';
import type { MatchLaunch } from '../game/GameView';
import { playMusic } from '../game/audio/music';
import { audio } from '../game/audio/Audio';
import { navigateBack } from './back';
import { portalChatDisabled } from './crazygames';
import { settings } from './settings';
import './viewport';
import { refreshMe } from './state/actions';
import { go, openModal, route, type Screen } from './state/nav';
import { exitNotice, launch, pendingLevelUp, roomReady, setReportHandler, startDevOnline, startOffline } from './state/play';
import { prefs, updatePrefs } from './state/prefs';
import { boot, onboardingStep, phase, updateMessage } from './state/session';
import { wireAnalytics } from './state/telemetry';
import { conn, currentMatch, lastRewards, me, party, queue, toast } from './state/store';
import { wireSocial } from './state/wire';
import { todayKey } from './ui/format';
import { alertDialog } from './ui/kit';
import { ModalLayer, QueuePill, Toasts } from './ui/layers';
import { RotateOverlay } from './ui/RotateOverlay';
import { ArmoryScreen } from './screens/Armory';
import { BattlePassScreen } from './screens/BattlePass';
import { BannedScreen, BootScreen, Logo, UpdateScreen, VoxelLoader } from './screens/Boot';
import { ChatDrawer } from './screens/Chat';
import { ClanScreen } from './screens/Clans';
import { DailyLogin } from './screens/DailyLogin';
import { FriendsScreen } from './screens/Friends';
import { InboxScreen } from './screens/Inbox';
import { LeaderboardsScreen } from './screens/Leaderboards';
import { LegalScreen, openLegal, type LegalDocId } from './screens/Legal';
import { openResetPassword, takeResetToken } from './screens/PasswordReset';
import { Lobby } from './screens/Lobby';
import { Onboarding } from './screens/Onboarding';
import { PlayScreen, RoomReadyDialog } from './screens/Play';
import { openReport } from './screens/Profile';
import { QuestsScreen } from './screens/Quests';
import { LevelUpPanel, ResultsScreen } from './screens/Results';
import { SettingsScreen } from './screens/Settings';
import { StoreScreen } from './screens/Store';
import { WardrobeScreen } from './screens/Wardrobe';

export { startDevOnline, startOffline } from './state/play';

const params = new URLSearchParams(location.search);

// Automation hooks (used by the test tools): ?auto=practice&mode=tdm&map=brick_plaza&bots=8  or  ?auto=online
const auto = params.get('auto');
if (auto === 'practice') {
  startOffline((params.get('mode') as ModeId) ?? 'tdm', params.get('map') ?? 'brick_plaza', Number(params.get('bots') ?? 8), { starter: true, name: 'You' });
}
if (auto === 'online') startDevOnline((params.get('mode') as ModeId) ?? 'tdm', params.get('map') ?? 'brick_plaza', params.get('name') ?? 'Tester');

// Links from the website: ?legal=privacy opens the document over whatever screen is showing.
const legalDoc = params.get('legal') as LegalDocId | null;
if (legalDoc && ['privacy', 'terms', 'parents', 'credits'].includes(legalDoc)) queueMicrotask(() => openLegal(legalDoc));
// The "forgot password" email's link: #reset=<token> (also when it lands on an already open game).
const openReset = (): void => {
  const token = takeResetToken();
  if (token) queueMicrotask(() => openResetPassword(token));
};
openReset();
addEventListener('hashchange', openReset);

// Menu audio follows the settings everywhere (the match view re-applies them too).
effect(() => {
  const s = settings.value;
  audio.setVolumes(s.masterVolume, s.sfxVolume, s.musicVolume);
});

// Menu music whenever no match is running (matches pick a map track and end on a stinger;
// playMusic defers until the first gesture unlocks audio).
effect(() => {
  if (!launch.value) playMusic('menu', { fade: 1.5 });
});

// Browsers only allow audio after a gesture: resume on the first interaction.
addEventListener('pointerdown', () => audio.unlock(), { capture: true });
addEventListener('keydown', () => audio.unlock(), { capture: true, once: true });

wireSocial();
wireAnalytics({
  screen: computed(() => route.value.screen),
  inMatch: computed(() => !!launch.value),
  onboarding: computed(() => (phase.value === 'onboarding' ? onboardingStep.value : null)),
});
// Reports from the in-match pause menu open the regular report dialog over the match.
setReportHandler((targetId, name, matchId) => openReport({ targetId, name, matchId }));

// Dev-only automation handle (like window.__vmGame): lets test scripts inspect and drive menu state.
if (import.meta.env.DEV) {
  (window as unknown as { __vmMeta?: unknown }).__vmMeta = { route, go, me, conn, party, queue, lastRewards, currentMatch, pendingLevelUp, toast, openModal, launch };
}

// The match view (Three.js renderer, world, workers) is split into its own chunk.
type GameViewModule = typeof import('../game/GameView');
let gameViewModule: Promise<GameViewModule> | null = null;
function loadGameView(): Promise<GameViewModule> {
  return (gameViewModule ??= import('../game/GameView'));
}

const launchKeys = new WeakMap<MatchLaunch, number>();
let launchSeq = 0;
function launchKey(l: MatchLaunch): number {
  let k = launchKeys.get(l);
  if (k === undefined) {
    k = ++launchSeq;
    launchKeys.set(l, k);
  }
  return k;
}

function MatchView(props: { launch: MatchLaunch }) {
  const [mod, setMod] = useState<GameViewModule | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    loadGameView()
      .then(setMod)
      .catch((e: Error) => setFailed(e.message));
  }, []);
  if (failed) {
    return (
      <div class="ui-root">
        <div class="boot">
          <div class="boot-inner">
            <Logo />
            <div class="boot-msg">Couldn't load the game: {failed}</div>
            <button class="btn primary" onClick={() => props.launch.onExit()}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!mod) {
    return (
      <div class="ui-root">
        <div class="boot">
          <div class="boot-inner">
            <Logo />
            <VoxelLoader />
          </div>
        </div>
      </div>
    );
  }
  return <mod.GameView launch={props.launch} />;
}

function ScreenView(props: { screen: Screen }) {
  switch (props.screen) {
    case 'play':
      return <PlayScreen />;
    case 'results':
      return <ResultsScreen />;
    case 'armory':
      return <ArmoryScreen />;
    case 'wardrobe':
      return <WardrobeScreen />;
    case 'store':
      return <StoreScreen />;
    case 'battlepass':
      return <BattlePassScreen />;
    case 'quests':
    case 'achievements':
      return <QuestsScreen />;
    case 'friends':
      return <FriendsScreen />;
    case 'clan':
      return <ClanScreen />;
    case 'leaderboards':
      return <LeaderboardsScreen />;
    case 'inbox':
      return <InboxScreen />;
    case 'settings':
      return <SettingsScreen />;
    case 'legal':
      return <LegalScreen />;
    default:
      return <Lobby />;
  }
}

/** Pops dialogs that depend on app state (daily login, level ups, disconnects, rooms). */
function Watchers() {
  const m = me.value;
  const screen = route.value.screen;
  const online = conn.value === 'online';

  useEffect(() => {
    if (!m || !online || screen !== 'lobby' || m.dailyLogin.claimedToday) return;
    const today = todayKey();
    if (prefs.value.dailyShown === today) return;
    updatePrefs({ dailyShown: today });
    const t = setTimeout(() => openModal((close) => <DailyLogin close={close} />), 600);
    return () => clearTimeout(t);
  }, [m?.id, online, screen, m?.dailyLogin.claimedToday]);

  const lvl = pendingLevelUp.value;
  useEffect(() => {
    if (!lvl || screen === 'results') return;
    pendingLevelUp.value = null;
    openModal((close) => <LevelUpPanel from={lvl.from} to={lvl.to} rewards={lvl.rewards} onClose={close} />);
  }, [lvl, screen]);

  const notice = exitNotice.value;
  useEffect(() => {
    if (!notice) return;
    exitNotice.value = null;
    alertDialog('Match ended', notice);
  }, [notice]);

  const room = roomReady.value;
  useEffect(() => {
    if (!room) return;
    openModal((close) => <RoomReadyDialog close={close} />, { onDismiss: () => (roomReady.value = null) });
  }, [room?.match.matchId]);

  return null;
}

function MainShell() {
  const r = route.value;
  const s = settings.value;
  useEffect(() => {
    // Warm the match chunk while the player browses the menus.
    const idle = (window as unknown as { requestIdleCallback?: (fn: () => void) => number }).requestIdleCallback ?? ((fn: () => void) => setTimeout(fn, 1500));
    idle(() => void loadGameView().catch(() => {}));
  }, []);
  return (
    <div class={`shell ${s.reduceMotion ? 'reduce-motion' : ''}`}>
      <div class="shell-bg" aria-hidden="true">
        <div class="shell-grid" />
        {Array.from({ length: 10 }, (_, i) => (
          <i key={i} class="floaty" style={{ left: `${(i * 41 + 7) % 100}%`, animationDelay: `${-i * 2.3}s`, animationDuration: `${16 + (i % 4) * 4}s` }} />
        ))}
      </div>
      <div class="shell-screen" key={r.screen}>
        <ScreenView screen={r.screen} />
      </div>
      {s.chatEnabled && !portalChatDisabled.value && <ChatDrawer />}
      <QueuePill />
      <Watchers />
    </div>
  );
}

function onKey(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  navigateBack();
}

export function App() {
  const l = launch.value;
  const p = phase.value;

  useEffect(() => {
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  // Boot once no automation match is running (tests start straight into a match).
  const [booted, setBooted] = useState(false);
  useEffect(() => {
    if (booted || l) return;
    setBooted(true);
    void boot();
    const purchase = params.get('purchase');
    if (purchase) {
      const url = new URL(location.href);
      url.searchParams.delete('purchase');
      history.replaceState(null, '', url.toString());
      if (purchase === 'done') {
        toast('Thanks for your purchase!', { kind: 'reward', body: 'Your gems will arrive in a moment.' });
        setTimeout(() => void refreshMe(), 2500);
      }
    }
  }, [l, booted]);

  // Menus live in .ui-root, which scales them to fit short screens (see viewport.ts); the match
  // view is never scaled, only the dialogs and toasts shown over it.
  if (l) {
    return (
      <>
        <MatchView key={launchKey(l)} launch={l} />
        <div class="ui-root overlay">
          <ModalLayer />
          <Toasts />
        </div>
        <RotateOverlay />
      </>
    );
  }
  return (
    <>
      <div class="ui-root">
        {updateMessage.value ? (
          <UpdateScreen />
        ) : (
          <>
            {p === 'boot' && <BootScreen />}
            {p === 'onboarding' && <Onboarding />}
            {p === 'banned' && <BannedScreen />}
            {p === 'main' && <MainShell />}
          </>
        )}
        <ModalLayer />
        <Toasts />
      </div>
      <RotateOverlay />
    </>
  );
}
